// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapporter & indberetning (PLAN.md layout contract; v8 prototype
 * `rightRapport` and `pageHtml`), which also holds the old Indberetning
 * screen:
 *
 *  - left: the tree (`bygning/rapportTree.ts`) — the Ressourcekortlægning's
 *    and the Miljøkortlægning's pages, each Klar or Ufuldstændig (ruxd's
 *    section verdict when it serves the preview, contract gap 4, else the
 *    checklist's), and Indberetning's leaves: bygningsaffald.dk and
 *    Nedrivningsplan (Kommer snart), the CSV export and, behind the
 *    `submission` flag, the Materialeflow; J/K walk the leaves;
 *  - centre: the leaf — a report page as ruxd compiles it (PNG pages,
 *    re-asked ~1.5 s after a change; composed from the live case on an older
 *    ruxd, and always for the Miljøkortlægning's Forside and Sagsdata), the
 *    CSV export (the template CSV with its saved options), "Kommer snart",
 *    or the Materialeflow over the pkt. 10 fractions;
 *  - right: the report the leaf belongs to (an Indberetning leaf's is the
 *    Ressourcekortlægning) — "Rapport vN · Kladde/Komplet", the Forside's
 *    cover image on the Forside, and its versions (Ressourcetabel +
 *    "Generér ny version" on the report chain, with whether it will be a
 *    kladde); the Miljøkortlægning is not generated on its own yet. Behind
 *    the `submission` flag also the Del 1 / Del 2 checklist for
 *    bygningsaffald.dk (`bygning/submission.ts`), each row a jump to where it
 *    is fixed, and "Indberet til bygningsaffald.dk", open only when the
 *    checklist is ready.
 *
 * What blocks the report is Gennemgang's left tile now: the jumps there
 * switch mode. The CSV-option writes and the generate guard are the retired
 * RapportPage's, moved here unchanged (and hidden from a viewer,
 * `useCanEdit`): CSV options save back to the template on the app write
 * chain, only the newest write per template reconciles, and a double click
 * cannot queue two generations.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import type { Template, TemplateCsv } from '../../api/types';
import { useCanEdit } from '../../app/CaseRoleContext';
import { useJobs } from '../../app/JobsContext';
import { useReportCover } from '../../app/bygning/useReportCover';
import { useReportPreview } from '../../app/bygning/useReportPreview';
import { storedFlags } from '../../app/featureFlags';
import { SKABELONER_PATH } from '../../app/links';
import { createOnceGuard } from '../../app/onceGuard';
import { useMutationQueue } from '../../app/useMutationQueue';
import { buildChecklist, indberetHint, projectRecord } from '../../bygning/checklist';
import { reportHeading, type ReportHeading } from '../../bygning/pages';
import {
  leafOf,
  leafOfSection,
  leafPageModel,
  leafPatch,
  MILJOE_NOTE,
  REPORT_TITLE,
  reportOfLeaf,
  SOON_DETAIL,
  SOON_TITLE,
  treeModel,
} from '../../bygning/rapportTree';
import { previewPpi, serverPageModels, withServerSections } from '../../bygning/reportPreview';
import { buildSubmission, type SubmissionTarget } from '../../bygning/submission';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Pill } from '../../components/Pill';
import { Spinner } from '../../components/Spinner';
import { TextLink } from '../../components/TextLink';
import { CoverPanel } from '../../components/bygning/rapport/CoverPicker';
import { PagePreview } from '../../components/bygning/rapport/PagePreview';
import { RapportTree } from '../../components/bygning/rapport/RapportTree';
import { SubmissionChecklist } from '../../components/bygning/rapport/SubmissionChecklist';
import { useSagsdataExtras } from '../../components/bygning/sagsdata/useSagsdataExtras';
import { DataExportPanel } from '../../components/rapport/DataExportPanel';
import { TemplateSelect } from '../../components/rapport/TemplateSelect';
import { VersionList } from '../../components/rapport/VersionList';
import { SEND_NOTICE } from '../../indberetning/model';
import { type CsvOptions, writeCsvOptions } from '../../rapport/csvOptions';
import {
  csvWriteOutcome,
  defaultExportTemplateId,
  generateForecast,
  REPORT_FOOTNOTE,
  ressourcetabelHint,
  validChoice,
} from '../../rapport/model';
import { createLatestGate, replaceTemplate, templateErrorMessage } from '../../skabeloner/model';
import type { ModeComponent } from './modeTypes';
import styles from './RapportMode.module.css';

const INDBERET_HINT_ID = 'rapport-indberet-hint';
const FORECAST_ID = 'rapport-generate-forecast';

/**
 * Brings a place in the right tile into view and focus (a heading needs
 * `tabIndex` -1). The tile is a scrolling panel on desktop and a fixed-height
 * sheet on a phone — it never collapses — so scrolling it is enough;
 * `center` keeps the target clear of the sticky Indberet bar at the tile's
 * foot, which `nearest` would leave it under. A closed Del (a `<details>`)
 * around it is opened first.
 */
function reveal(el: HTMLElement | null) {
  if (!el) return;
  const closed = el.closest('details:not([open])');
  if (closed instanceof HTMLDetailsElement) closed.open = true;
  el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
}

export const RapportMode: ModeComponent = ({ data, sel, setSel, actions }) => {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  // Read once per mount: a changed flag takes a reload.
  const [flags] = useState(storedFlags);
  const treeFlags = useMemo(() => ({ materialeflow: flags.submission }), [flags]);
  // The project record with the BEK 496 Sagsdata fields: the server's
  // (ruxd-6tz), else the copy kept in this browser — merged as Overblik
  // merges them, so both modes judge the same record.
  const { extras } = useSagsdataExtras(data.project);
  const project = useMemo(() => projectRecord(data.project, extras), [data.project, extras]);

  const clientChecklist = useMemo(
    () =>
      buildChecklist({
        types: data.types,
        samples: data.samples,
        fractions: data.fractions,
        project,
        resourceKeys: data.resourceKeys,
        resources: data.resources,
      }),
    [data.types, data.samples, data.fractions, project, data.resourceKeys, data.resources],
  );

  // ------------------------------------------------- templates (RapportPage) --

  const [templates, setTemplates] = useState<Template[]>(data.templates);
  const [pdfTemplate, setPdfTemplate] = useState<number | null>(null);
  const [exportTemplate, setExportTemplate] = useState<number | null>(null);
  useEffect(() => {
    const list = data.templates;
    setTemplates(list);
    setExportTemplate((id) => validChoice(list, id) ?? defaultExportTemplateId(list));
    setPdfTemplate((id) => validChoice(list, id));
  }, [data.templates]);

  const csvQueue = useMutationQueue({ onError: (cause) => actions.notify(templateErrorMessage(cause)) });
  const csvGate = useRef(createLatestGate()).current;
  const onCsvChange = (patch: Partial<CsvOptions>) => {
    const t = templates.find((x) => x.id === exportTemplate);
    if (!t || !canEdit) return;
    const csv = writeCsvOptions(t.csv, patch) as TemplateCsv;
    const ticket = csvGate.next(t.id);
    setTemplates((prev) => replaceTemplate(prev, { ...t, csv }));
    void csvQueue.mutate(async () => {
      try {
        const saved = await api.patchTemplate(t.id, { csv });
        if (csvWriteOutcome(csvGate.isLatest(t.id, ticket), true) === 'apply') {
          setTemplates((prev) => replaceTemplate(prev, saved));
        }
      } catch (cause) {
        if (csvWriteOutcome(csvGate.isLatest(t.id, ticket), false) === 'reconcile') {
          const fresh = await api.templates().catch(() => null);
          setTemplates((prev) => fresh ?? replaceTemplate(prev, t));
        }
        throw cause;
      }
    });
  };

  // ------------------------------------------------- the server's preview --

  const cover = useReportCover(actions.notify);
  const { surveyChange } = useJobs();
  const ppi = useMemo(() => previewPpi(typeof window === 'undefined' ? 1 : window.devicePixelRatio), []);
  // Any of these changing means the report may read differently: re-ask (debounced).
  const revision = useMemo(
    () => ({}),
    [data.types, data.samples, data.project, data.resources, data.resourceKeys, data.photos, cover.version, surveyChange],
  );
  const preview = useReportPreview({
    ppi,
    templateId: validChoice(templates, pdfTemplate),
    revision,
    enabled: data.loaded,
  });
  const sections = preview.mode === 'server' ? preview.sections : null;
  const checklist = useMemo(() => withServerSections(clientChecklist, sections, data.fractions), [clientChecklist, sections, data.fractions]);
  const pages = useMemo(() => serverPageModels(checklist, data.reportVersions, sections), [checklist, data.reportVersions, sections]);

  // ------------------------------------------------------------- the tree --

  const leaf = leafOf(sel.page, sel.rapport, treeFlags);
  const groups = useMemo(() => treeModel(pages, treeFlags, leaf.id), [pages, treeFlags, leaf.id]);
  const report = reportOfLeaf(leaf.id);
  const heading: ReportHeading = report === 'ressource' ? reportHeading(data.reportVersions) : { title: REPORT_TITLE.miljoe, pill: null };

  // ------------------------------------------------------------ generate --

  // `reportBusy` only disables the button after a re-render; the guard closes
  // the double-click gap. Until the first list read lands, generating waits.
  const [generating] = useState(createOnceGuard);
  const listReady = data.reportVersions !== null;
  const generate = () => {
    if (!listReady || !canEdit) return;
    generating.run(() => actions.generateReport(validChoice(templates, pdfTemplate)));
  };

  // ----------------------------------------------------------- indberet --

  const [sendNotice, setSendNotice] = useState(false);
  const hint = indberetHint(checklist);
  const indberet = () => {
    if (!checklist.ready) return;
    setSendNotice(true);
    if (leaf.id !== 'indberetning.materialeflow') setSel(leafPatch('indberetning.materialeflow'));
  };

  // ---------------------------------------------------- bygningsaffald.dk --

  // Loaded, not loading, and still null: the read failed.
  const photosFailed = data.photos === null && data.loaded && !data.loading;
  // Only behind the flag: nothing shows it otherwise.
  const submission = useMemo(
    () =>
      flags.submission
        ? buildSubmission(checklist, {
            types: data.types,
            samples: data.samples,
            photos: data.photos,
            photosFailed,
            fractions: data.fractions,
            versions: data.reportVersions,
            project,
            resourceKeys: data.resourceKeys,
            resources: data.resources,
          })
        : null,
    [
      flags.submission,
      checklist,
      data.types,
      data.samples,
      data.photos,
      photosFailed,
      data.fractions,
      data.reportVersions,
      project,
      data.resourceKeys,
      data.resources,
    ],
  );
  const forecast = generateForecast(data.fractions);

  const generateRef = useRef<HTMLButtonElement>(null);
  const versionsRef = useRef<HTMLHeadingElement>(null);
  const openType = (typeId: number) => setSel({ mode: 'ressourcer', typeId });
  // "Det, der blokerer" is Gennemgang's list.
  const showBlocking = () => setSel({ mode: 'gennemgang' });
  // A row leads to another mode, or to a place in this tile. A viewer (or a
  // button still waiting for the list) has no generate button to focus: the
  // versions are the nearest place.
  const onGo = (target: SubmissionTarget) => {
    if (target.kind === 'sel') setSel({ mode: target.sel.mode, ...target.sel.patch });
    else if (target.kind === 'blocking') showBlocking();
    else if (target.kind === 'generate' && generateRef.current && !generateRef.current.disabled) reveal(generateRef.current);
    else reveal(versionsRef.current);
  };

  const versions =
    report === 'ressource' ? (
      <section className={styles.section} aria-labelledby="rapport-versioner">
        <h3 id="rapport-versioner" ref={versionsRef} tabIndex={-1} className={styles.sectionHeading}>
          Versioner
        </h3>
        {canEdit && (
          <div className={styles.generate}>
            <TemplateSelect
              id="rapport-ressourcetabel"
              label="Ressourcetabel"
              templates={templates}
              value={validChoice(templates, pdfTemplate)}
              onChange={setPdfTemplate}
              allowNone
              disabled={data.reportBusy}
              onEditTemplates={() => navigate(SKABELONER_PATH)}
              variant="filled"
              size="lg"
            />
            <Button
              ref={generateRef}
              size="lg"
              onClick={generate}
              disabled={data.reportBusy || !listReady}
              aria-describedby={forecast ? FORECAST_ID : undefined}
            >
              {data.reportBusy ? 'Genererer…' : 'Generér ny version'}
            </Button>
            {forecast && (
              <p id={FORECAST_ID} className={`${styles.forecast} ${styles[`forecast_${forecast.tone}`]}`}>
                {forecast.text}
              </p>
            )}
            <p className={styles.hint}>
              {ressourcetabelHint(templates.find((t) => t.id === validChoice(templates, pdfTemplate)) ?? null)}
            </p>
          </div>
        )}
        {data.reportVersions === null ? (
          <div className={styles.inline}>
            <Spinner label="Indlæser versioner…" />
            {data.loaded && !data.loading && (
              <TextLink size="sm" onClick={actions.refresh}>
                Hent igen
              </TextLink>
            )}
          </div>
        ) : (
          <>
            {data.reportVersions.length === 0 && (
              <p className={styles.hint}>Ingen versioner endnu — generér den første; den gemmes i projektet.</p>
            )}
            <VersionList versions={data.reportVersions} pdfUrl={(id) => api.reportPdfUrl(id)} inventoryUrl={api.csvExportUrl()} />
          </>
        )}
      </section>
    ) : (
      <section className={styles.section} aria-labelledby="rapport-versioner">
        <h3 id="rapport-versioner" ref={versionsRef} tabIndex={-1} className={styles.sectionHeading}>
          Versioner
        </h3>
        <p className={styles.note}>{MILJOE_NOTE}</p>
      </section>
    );

  const right = (
    <div className={styles.right}>
      <div className={styles.title}>
        <h2 className={styles.heading}>{heading.title}</h2>
        {heading.pill && (
          <Pill tone={heading.pill.tone} title={heading.pill.title}>
            {heading.pill.text}
          </Pill>
        )}
      </div>

      {submission && <SubmissionChecklist submission={submission} onGo={onGo} />}

      {/* The cover image is the compiled report's: the Ressourcekortlægning's Forside. */}
      {leaf.id === 'ressource.forside' && <CoverPanel state={cover} />}

      {versions}

      <p className={styles.footnote}>{REPORT_FOOTNOTE}</p>

      {flags.submission && (
        <div className={styles.actions}>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!checklist.ready}
            title={hint ?? undefined}
            aria-describedby={hint ? INDBERET_HINT_ID : undefined}
            onClick={indberet}
          >
            Indberet til bygningsaffald.dk
          </Button>
          {hint && (
            <span id={INDBERET_HINT_ID} className={styles.gateHint}>
              {hint}
            </span>
          )}
          {sendNotice && (
            <p className={styles.notice} role="status">
              {SEND_NOTICE}
            </p>
          )}
        </div>
      )}
    </div>
  );

  let centre: ReactNode;
  if (leaf.kind === 'csv') {
    centre = (
      <div className={styles.sheet}>
        <DataExportPanel
          templates={templates}
          selectedId={validChoice(templates, exportTemplate)}
          onSelect={setExportTemplate}
          onCsvChange={onCsvChange}
          writing={csvQueue.busy}
          csvUrl={(id) => api.resourcesExportCsvUrl(id)}
          onDownloadError={actions.notify}
        />
      </div>
    );
  } else if (leaf.kind === 'soon') {
    centre = (
      <div className={styles.sheet}>
        <EmptyState bare title={SOON_TITLE} detail={SOON_DETAIL[leaf.id]} />
      </div>
    );
  } else {
    // A document leaf, or the Materialeflow (the `indberetning` page).
    const model = leafPageModel(pages, leaf) ?? pages[0];
    centre = (
      <PagePreview
        model={model}
        report={report}
        data={data}
        project={project}
        onRetry={actions.refresh}
        preview={preview}
        ppi={ppi}
        cover={cover}
        onSection={(id) => {
          const to = leafOfSection(id);
          if (to) setSel(leafPatch(to));
        }}
        onOpenType={openType}
        onShowBlocking={showBlocking}
      />
    );
  }

  return {
    left: <RapportTree groups={groups} onSelect={(id) => setSel(leafPatch(id))} />,
    leftWide: true,
    leftPhoneBar: true,
    leftWithCentre: true,
    right,
    centre,
    hint: 'J/K næste/forrige side',
  };
};
